import { motion } from "framer-motion";
import { ChevronDown, Phone } from "lucide-react";
import { COMPANY, STATS } from "@/lib/data";
import { useBooking } from "@/context/BookingContext";
import { usePublicPricing } from "@/lib/usePublicPricing";

export const Hero = () => {
  const { openBooking } = useBooking();
  const pricing = usePublicPricing();

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden grain">
      {/* Background logo image */}
      <div
        className="absolute inset-0 bg-center bg-no-repeat bg-contain md:bg-cover"
        style={{ backgroundImage: `url(${COMPANY.logo})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/40 to-[#050505]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_0%,#050505_85%)]" />

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-5 md:px-10 pt-24">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#F2B705] font-bold"
        >
          {COMPANY.est} · St. Louis, MO
        </motion.span>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display font-black uppercase leading-[0.9] tracking-tighter mt-5 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl max-w-4xl"
        >
          Drive Today.<br /><span className="text-gold-gradient">Pay Less.</span> Stay Moving.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="font-script italic text-2xl md:text-3xl text-white/70 mt-6"
        >
          Affordable rentals · Flexible insurance · No deposit
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          data-testid="hero-package-pricing"
          className="flex flex-wrap items-stretch gap-3 mt-7"
        >
          <div className="rounded-lg border border-white/15 bg-black/40 backdrop-blur px-5 py-3">
            <div className="text-[10px] uppercase tracking-widest text-white/45">3-Day Rental Special</div>
            <div className="font-display font-black text-3xl text-gold-gradient leading-none mt-1">${pricing.three_day_package}</div>
            <div className="text-[11px] text-white/40 mt-1">every vehicle · 3 full days</div>
          </div>
          <div className="relative rounded-lg border border-[#F2B705] bg-[#F2B705]/[0.08] backdrop-blur px-5 py-3">
            <span className="absolute -top-2.5 left-4 bg-[#F2B705] text-black text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wide">Best Value</span>
            <div className="text-[10px] uppercase tracking-widest text-[#F2B705]/90">Weekly Special</div>
            <div className="font-display font-black text-3xl text-white leading-none mt-1">${pricing.weekly_special}</div>
            <div className="text-[11px] text-white/40 mt-1">7 days · save ${pricing.weekly_savings}</div>
          </div>
          <div className="rounded-lg border border-white/15 bg-black/40 backdrop-blur px-5 py-3">
            <div className="text-[10px] uppercase tracking-widest text-white/45">Additional Days</div>
            <div className="font-display font-black text-3xl text-white/85 leading-none mt-1">${pricing.additional_day_rate}<span className="text-sm text-white/40 font-normal">/day</span></div>
            <div className="text-[11px] text-white/40 mt-1">coverage &amp; add-ons separate</div>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="text-base md:text-lg text-white/60 mt-5 max-w-xl font-light"
        >
          Affordable daily &amp; weekly car rentals in St. Louis. Bring your own insurance
          for the lowest rates, or choose All-In Coverage. Fast approval, no standard
          security deposit, and rideshare-eligible vehicles available.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="flex flex-wrap items-center gap-4 mt-9"
        >
          <button
            data-testid="hero-book-now"
            onClick={() => openBooking("")}
            className="bg-[#F2B705] text-black font-bold px-8 py-4 rounded-md hover:bg-white transition-colors duration-300 text-lg"
          >
            Request a Reservation
          </button>
          <a
            href={COMPANY.phoneHref}
            data-testid="hero-call"
            className="flex items-center gap-2 border border-white/25 px-7 py-4 rounded-md font-semibold hover:bg-white hover:text-black transition-colors duration-300"
          >
            <Phone size={18} /> {COMPANY.phone}
          </a>
        </motion.div>
      </div>

      {/* Stats bar */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.6 }}
        className="relative z-10 mx-auto max-w-[1400px] w-full px-5 md:px-10 mt-16 md:mt-24"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-lg overflow-hidden">
          {STATS.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().split(" ")[0]}`} className="bg-[#0A0A0A] px-6 py-7 text-center md:text-left">
              <div className="font-display font-black text-4xl md:text-5xl text-gold-gradient">{s.value}</div>
              <div className="text-xs uppercase tracking-widest text-white/45 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </motion.div>

      <div className="relative z-10 flex justify-center mt-10 pb-8 text-white/30">
        <ChevronDown className="animate-bounce" />
      </div>
    </section>
  );
};
